import React, { useState, useEffect } from 'react';
import {
  Card,
  Input,
  Button,
  Space,
  Typography,
  Tag,
  Popconfirm,
  message,
  Tooltip,
  Row,
  Col,
} from 'antd';
import './TopicsManager.css';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  BulbOutlined,
} from '@ant-design/icons';

const { Text, Paragraph } = Typography;

interface Topic {
  id: string;
  name: string;
  description?: string;
}

interface TopicsManagerProps {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
}

export const TopicsManager: React.FC<TopicsManagerProps> = ({
  value = '',
  onChange,
  disabled = false,
}) => {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [newTopic, setNewTopic] = useState('');
  const [editingTopic, setEditingTopic] = useState<{ id: string; name: string } | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  // Parse topics from string value (comma-separated)
  useEffect(() => {
    if (value) {
      const topicNames = value.split(',').map(t => t.trim()).filter(t => t);
      const parsedTopics = topicNames.map((name, index) => ({
        id: `topic-${index}`,
        name,
        description: '',
      }));
      setTopics(parsedTopics);
    } else {
      setTopics([]);
    }
  }, [value]);

  // Update parent form when topics change
  const updateParentValue = (newTopics: Topic[]) => {
    const topicString = newTopics.map(t => t.name).join(', ');
    onChange?.(topicString);
  };

  const addTopic = () => {
    if (!newTopic.trim()) {
      message.warning('Please enter a topic name');
      return;
    }

    if (topics.some(t => t.name.toLowerCase() === newTopic.trim().toLowerCase())) {
      message.warning('This topic already exists');
      return;
    }

    const newTopicObj: Topic = {
      id: `topic-${Date.now()}`,
      name: newTopic.trim(),
      description: '',
    };

    const updatedTopics = [...topics, newTopicObj];
    setTopics(updatedTopics);
    updateParentValue(updatedTopics);
    setNewTopic('');
    setIsAdding(false);
    message.success('Topic added successfully');
  };

  const deleteTopic = (id: string) => {
    const updatedTopics = topics.filter(t => t.id !== id);
    setTopics(updatedTopics);
    updateParentValue(updatedTopics);
    message.success('Topic deleted successfully');
  };

  const startEditing = (topic: Topic) => {
    setEditingTopic({ id: topic.id, name: topic.name });
  };

  const saveEdit = () => {
    if (!editingTopic || !editingTopic.name.trim()) {
      message.warning('Please enter a topic name');
      return;
    }

    if (topics.some(t => t.id !== editingTopic.id && t.name.toLowerCase() === editingTopic.name.trim().toLowerCase())) {
      message.warning('This topic name already exists');
      return;
    }

    const updatedTopics = topics.map(t =>
      t.id === editingTopic.id ? { ...t, name: editingTopic.name.trim() } : t
    );
    setTopics(updatedTopics);
    updateParentValue(updatedTopics);
    setEditingTopic(null);
    message.success('Topic updated successfully');
  };

  const cancelEdit = () => {
    setEditingTopic(null);
  };

  const handleKeyPress = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      action();
    }
  };

  return (
    <Card
      size="small"
      className="topics-manager"
      title={
        <Space>
          <BulbOutlined />
          <Text strong>Chat Topics</Text>
        </Space>
      }
      extra={
        !disabled && (
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => setIsAdding(true)}
            disabled={isAdding}
          >
            Add Topic
          </Button>
        )
      }
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Paragraph type="secondary" style={{ margin: 0, fontSize: '12px' }}>
          Define the main topics that your assistant can discuss in conversations. 
          These topics help guide the assistant's responses and expertise areas.
        </Paragraph>

        {/* Add new topic */}
        {isAdding && (
          <Row gutter={[8, 8]} align="middle">
            <Col flex="auto">
              <Input
                placeholder="Enter topic name (e.g., Product Support, Order Tracking, Returns)"
                value={newTopic}
                onChange={(e) => setNewTopic(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, addTopic)}
                autoFocus
                maxLength={50}
                showCount
              />
            </Col>
            <Col>
              <Space>
                <Button
                  type="primary"
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={addTopic}
                >
                  Add
                </Button>
                <Button
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => {
                    setIsAdding(false);
                    setNewTopic('');
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </Col>
          </Row>
        )}

        {/* Topics list */}
        <div className="topics-list">
          {topics.length === 0 ? (
            <div className="empty-state">
              <Text type="secondary">
                No topics defined yet. Add topics to help guide your assistant's conversations.
              </Text>
            </div>
          ) : (
            <Space wrap size={[8, 8]}>
              {topics.map((topic) => (
                <Tag
                  key={topic.id}
                  closable={!disabled}
                  onClose={() => deleteTopic(topic.id)}
                  style={{
                    padding: '4px 8px',
                    borderRadius: '6px',
                    border: '1px solid var(--ant-colorSplit)',
                    backgroundColor: 'var(--ant-colorBgContainer)',
                  }}
                >
                  {editingTopic?.id === topic.id ? (
                    <Space size={4}>
                      <Input
                        size="small"
                        value={editingTopic.name}
                        onChange={(e) => setEditingTopic({ ...editingTopic, name: e.target.value })}
                        onKeyPress={(e) => handleKeyPress(e, saveEdit)}
                        style={{ width: '120px' }}
                        autoFocus
                        maxLength={50}
                        showCount
                      />
                      <Button
                        type="text"
                        size="small"
                        icon={<CheckOutlined />}
                        onClick={saveEdit}
                      />
                      <Button
                        type="text"
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={cancelEdit}
                      />
                    </Space>
                  ) : (
                    <Space size={4}>
                      <span>{topic.name}</span>
                      {!disabled && (
                        <Tooltip title="Edit topic">
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => startEditing(topic)}
                            style={{ padding: 0, height: 'auto' }}
                          />
                        </Tooltip>
                      )}
                    </Space>
                  )}
                </Tag>
              ))}
            </Space>
          )}
        </div>

        {/* Suggested topics */}
        {topics.length === 0 && !isAdding && (
          <div style={{ marginTop: '8px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <strong>Suggested topics:</strong> Customer Support, Product Information, 
              Order Status, Returns & Refunds, Technical Help, Billing Questions
            </Text>
          </div>
        )}
      </Space>
    </Card>
  );
};
